import React, { FC, useEffect } from 'react';
import { Box, HStack, IconButton } from '@chakra-ui/react';
import { Token } from '@app/types/token';
import { WizardStepper } from '@app/screens/liquidity/LiquidityWizard/components/WizardStepper';

import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';
import { SelectTokensStep } from '@app/screens/liquidity/LiquidityWizard/components/SelectTokensStep';
import { SelectModeStep } from '@app/screens/liquidity/LiquidityWizard/components/SelectModeStep';
import { CompleteStep } from '@app/screens/liquidity/LiquidityWizard/components/CompleteStep';
import { useDerivedMintInfo } from '@app/screens/liquidity/LiquidityWizard/hooks/useDerivedMintInfo';
import { CloseIcon } from '@chakra-ui/icons';
import { APP_ROUTES } from '@app/constants/routes';
import { useRouter } from 'next/router';
import { getCurrencyFromKimToken } from '@app/helpers/currency';
import { useSupportedChain } from '@app/hooks/other/useSupportedChain';
import { StrategyProvider } from '@app/state/mintStore';

interface Props {
  initialData?: {
    from: {
      token: Token;
      amount: string;
    };
    to: {
      token: Token;
      amount: string;
    };
  };
}

export const LiquidityWizard: FC<Props> = ({ initialData }) => {
  const {
    step,
    currencyA,
    currencyB,
    strategy,
    provider,
    chainId: contextChainId,
    actions: {
      setCurrencyA,
      setCurrencyB,
      setStrategy,
      setProvider,
      setMode,
      setStep,
      reset,
      setChainId
    }
  } = useLiquidityWizardState();

  const router = useRouter();
  const mintInfo = useDerivedMintInfo();
  const chainId = useSupportedChain()?.chainId;

  useEffect(() => {
    if (
      initialData?.from.token &&
      initialData.to.token &&
      !currencyA &&
      !currencyB &&
      chainId
    ) {
      const _token0 = getCurrencyFromKimToken(initialData.from.token, chainId);
      const _token1 = getCurrencyFromKimToken(initialData.to.token, chainId);

      if (_token0 && _token1) {
        setCurrencyA(_token0);
        setCurrencyB(_token1);
      }

      if (router.query.provider && !provider) {
        setMode('auto');
        setProvider(router.query.provider as StrategyProvider);
      }

      if (router.query.vaultId && !strategy) {
        setStrategy(router.query.vaultId as string);
      }
    }
  }, [
    currencyA,
    currencyB,
    initialData,
    chainId,
    strategy,
    router.query.provider,
    router.query.vaultId,
    provider,
    setCurrencyA,
    setCurrencyB,
    setMode,
    setProvider,
    setStrategy
  ]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [
    setCurrencyA,
    setCurrencyB,
    setMode,
    setProvider,
    setStep,
    setStrategy,
    reset
  ]);

  useEffect(() => {
    if (contextChainId && chainId && contextChainId !== chainId) {
      reset();
    }
  }, [chainId, contextChainId, reset]);

  useEffect(() => {
    setChainId(chainId);
  }, [chainId]);

  return (
    <Box mx="auto" w="100%">
      <HStack justifyContent="flex-end" mb={2}>
        <IconButton
          aria-label="close"
          onClick={() => router.push(APP_ROUTES.poolsPage)}
        >
          <CloseIcon />
        </IconButton>
      </HStack>
      <WizardStepper />
      {step === 0 && <SelectTokensStep mintInfo={mintInfo} />}
      {step === 1 && <SelectModeStep mintInfo={mintInfo} />}
      {step === 2 && <CompleteStep mintInfo={mintInfo} />}
    </Box>
  );
};
